import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Mail, Clock, ChevronDown, ChevronUp, Paperclip } from "lucide-react";
import { toast } from "sonner";

const ACCOUNTS = {
  "sales@hitechsilico.com": { label: "Sales", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  "accounts@hitechsilico.com": { label: "Accounts", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "dispatch@hitechsilico.com": { label: "Dispatch", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  "info@hitechsilico.com": { label: "Info", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const fmtWhen = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} · ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
};

export const EmailLogDialog = ({ customer, onClose }) => {
  const [logs, setLogs] = useState(null);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState("");
  const [previews, setPreviews] = useState({});

  const toggleRow = async (l) => {
    const next = openId === l.id ? "" : l.id;
    setOpenId(next);
    if (next && !l.html && previews[l.id] === undefined) {
      setPreviews((p) => ({ ...p, [l.id]: null }));
      try {
        const r = await api.get(`/customers/email-log/${l.id}/preview`);
        setPreviews((p) => ({ ...p, [l.id]: r.data }));
      } catch { setPreviews((p) => ({ ...p, [l.id]: { reconstructed: false, html: "", attachments: [] } })); }
    }
  };

  const viewAttachment = async (lid, idx) => {
    try {
      const r = await api.get(`/customers/email-log/${lid}/attachment/${idx}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Attachment not stored for this email (logged before attachments were saved)"); }
  };

  const openDocPdf = async (url) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open the document PDF"); }
  };

  useEffect(() => {
    if (!customer) return;
    setLogs(null); setQ(""); setOpenId(""); setPreviews({});
    api.get(`/customers/${customer.id}/email-log`).then((r) => setLogs(r.data)).catch(() => setLogs([]));
  }, [customer]);

  const shown = (logs || []).filter((l) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [l.subject, l.to, l.sender].some((f) => (f || "").toLowerCase().includes(s));
  });

  return (
    <Dialog open={!!customer} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-hidden flex flex-col content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right" data-testid="email-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail size={16} className="text-[#3B82F6]" /> Email Timeline — {customer?.company_name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, recipient, sender…" className="h-9" data-testid="email-log-search" />
          <div className="flex gap-1.5 flex-none">
            {Object.values(ACCOUNTS).map((a) => (
              <span key={a.label} className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${a.cls}`}>{a.label}</span>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0 -mr-2 pr-2">
          {logs === null && <p className="text-sm text-slate-400 text-center py-10">Loading…</p>}
          {logs !== null && shown.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-10" data-testid="email-log-empty">
              {q ? "No emails match your search." : "No emails logged for this customer yet — logging started with the Zoho integration."}
            </p>
          )}
          <div className="relative pl-5">
            {shown.length > 0 && <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" />}
            {shown.map((l) => {
              const acc = ACCOUNTS[l.sender] || { label: l.sender, cls: "bg-slate-100 text-slate-600 border-slate-200" };
              const isOpen = openId === l.id;
              return (
                <div key={l.id} className="relative py-2.5" data-testid={`email-log-row-${l.id}`}>
                  <div className="absolute -left-[17px] top-[18px] w-2.5 h-2.5 rounded-full bg-white border-2 border-[#3B82F6]" />
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${acc.cls}`}>{acc.label}</span>
                        <span className="text-sm font-medium text-slate-800 truncate">{l.subject}</span>
                        {l.attachments?.length > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-500"><Paperclip size={10} /> {l.attachments.length}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        To: {l.to}{l.cc?.length > 0 && <> · Cc: {l.cc.join(", ")}</>} · from {l.sender}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-none pt-0.5">
                      <span className="flex items-center gap-1 text-[11px] text-slate-400 whitespace-nowrap">
                        <Clock size={10} /> {fmtWhen(l.created_at)}
                      </span>
                      <button onClick={() => toggleRow(l)}
                              className="p-1 text-slate-400 hover:text-[#3B82F6] rounded hover:bg-blue-50 transition-colors duration-150"
                              title={isOpen ? "Collapse" : "View full email"} data-testid={`email-log-toggle-${l.id}`}>
                        {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                      </button>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden bg-slate-50/50" data-testid={`email-log-detail-${l.id}`}>
                      {l.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-2 px-3 py-2 border-b border-slate-200 bg-white">
                          {l.attachments.map((a, i) => (
                            <button key={i} onClick={() => viewAttachment(l.id, i)}
                                    className="inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-md px-2.5 py-1 text-slate-700 hover:border-[#3B82F6] hover:text-[#2563EB] transition-colors duration-150"
                                    data-testid={`email-log-att-${l.id}-${i}`}>
                              <Paperclip size={11} /> {a.filename}{a.size_kb ? <span className="text-slate-400">· {a.size_kb} KB</span> : null}
                            </button>
                          ))}
                        </div>
                      )}
                      {l.html ? (
                        <iframe title={`email-${l.id}`} srcDoc={l.html} sandbox=""
                                className="w-full bg-white" style={{ height: "420px", border: 0 }}
                                data-testid={`email-log-body-${l.id}`} />
                      ) : previews[l.id] === null || previews[l.id] === undefined ? (
                        <p className="text-xs text-slate-400 px-3 py-4">Loading email preview…</p>
                      ) : previews[l.id].html ? (
                        <>
                          {previews[l.id].reconstructed && (
                            <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-800" data-testid={`email-log-reconstructed-${l.id}`}>
                              Reconstructed preview — the original body wasn't stored (sent before full-email logging). This is the standard template rebuilt from the linked document.
                            </div>
                          )}
                          {(previews[l.id].attachments || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 px-3 py-2 border-b border-slate-200 bg-white">
                              {previews[l.id].attachments.map((a, i) => (
                                <button key={i} onClick={() => openDocPdf(a.url)}
                                        className="inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-md px-2.5 py-1 text-slate-700 hover:border-[#3B82F6] hover:text-[#2563EB] transition-colors duration-150"
                                        data-testid={`email-log-preview-att-${l.id}-${i}`}>
                                  <Paperclip size={11} /> {a.filename}
                                </button>
                              ))}
                            </div>
                          )}
                          <iframe title={`email-${l.id}`} srcDoc={previews[l.id].html} sandbox=""
                                  className="w-full bg-white" style={{ height: "420px", border: 0 }}
                                  data-testid={`email-log-body-${l.id}`} />
                        </>
                      ) : (
                        <p className="text-xs text-slate-400 px-3 py-4">Email body was not stored for this email and it could not be reconstructed from its subject.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
